import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api';

/**
 * The other names an artist releases under.
 *
 * ## A name, not an account and not a handle
 *
 * One person, several project names. It is a display name on a release and
 * nothing more: no login, no followers, no payouts, no page of its own. A track
 * credited to one still links to the profile that owns it, which is what lets
 * every component that renders an artist keep working untouched - they all draw
 * `display_name || @username`, and an alias only changes the first half.
 *
 * The alternative people reach for is a second account, and the platform is
 * built to make that a bad idea: every guard that stops somebody tipping
 * themselves, boosting their own queue or earning discovery credit off their
 * own catalogue compares two user ids.
 */
export interface Alias {
  id: number;
  name: string;
  /** How many releases carry it. Zero means it has never been used. */
  tracks: number;
}

/** Your own names, for the upload form and the settings card. */
export function useAliases(enabled = true) {
  return useQuery({
    queryKey: ['me', 'aliases'],
    queryFn: () => api.get<{ aliases: Alias[] }>('/me/aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useAddAlias() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => api.post<{ aliases: Alias[] }>('/me/aliases', { name }),
    onSuccess: (data) => client.setQueryData(['me', 'aliases'], data),
  });
}

export function useRemoveAlias() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.delete<{ aliases: Alias[] }>(`/me/aliases/${id}`),
    onSuccess: (data) => client.setQueryData(['me', 'aliases'], data),
  });
}
